import {
  AddonType,
  StepsType,
  useGetUserByEmailLazyQuery,
} from '@/affiliate-dashboard/graphql';
import { zodResolver } from '@hookform/resolvers/zod';
import { enqueueSnackbar } from 'notistack';
import { useEffect, type ReactNode } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';

const orderSchema = z.object({
  firstName: z.string().min(1, { message: 'First name is required' }),
  lastName: z.string().min(1, { message: 'Last name is required' }),
  email: z.string().min(1, { message: 'Email is required' }).email(),
  countryCode: z.string().min(1, { message: 'Country is required' }),
  password: z.string().optional(),
  termsPolicy: z.literal<boolean>(true),
  refundPolicy: z.literal<boolean>(true),
  initialBalance: z.number(),
  discountCode: z.string().optional(),
  addons: z.array(z.nativeEnum(AddonType)),
  accountType: z.nativeEnum(StepsType),
  city: z.string().optional(),
  street: z.string().optional(),
  zip: z.string().optional(),
});

export type Order = z.infer<typeof orderSchema>;

export function OrderFormWrapper({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const [getUserByEmail] = useGetUserByEmailLazyQuery();
  const methods = useForm<Order>({
    resolver: zodResolver(orderSchema),
    mode: 'onChange',
    defaultValues: {
      addons: [],
    },
  });

  useEffect(() => {
    const subscription = methods.watch((value, { name }) => {
      if (name === 'email' && value.email) {
        const timeout = setTimeout(async () => {
          try {
            const { data } = await getUserByEmail({
              variables: {
                email: value.email,
              },
            });

            if (data) {
              methods.reset({
                ...methods.getValues(),
                firstName: data?.userByEmail?.firstName ?? '',
                lastName: data?.userByEmail?.lastName ?? '',
                countryCode: data?.userByEmail?.countryCode ?? '',
              });
            }
          } catch (error) {
            enqueueSnackbar(
              'Error fetching user data. Please try again later.',
              {
                variant: 'error',
              },
            );
          }
        }, 500);

        return () => {
          clearTimeout(timeout);
        };
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [methods, getUserByEmail]);

  return <FormProvider {...methods}>{children}</FormProvider>;
}
